import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import { DefaultWithTitleLayout } from '../../components/templates/default-with-title-layout'
import { VixellTable } from '../../components/part/vixell-table'
import { Select } from '../../components/molecules/select'
import { SearchInput } from '../../components/molecules/search-input'
import { Lang } from '../../translations/lang'
import { webRequest } from '../../types/handle-api'
import { InHospitalInoutRowComponent } from '../../types/typescript-axios'
import { usePaging } from '../../contexts/use-paging'

const PATH = '/in-hospital/inout'

// 院内輸送 入出庫データ出力（T-002）。
// 仕様: vixell-serverless/docs/specifications/in-hospital-transportation（screens.md / apis.md）。
// 決定（MR-005）: 期間=秒指定（datetime-local、この画面のみ）/ タグID区切り=カンマ・空白。
//   ※ MR-003 の「日付のみ」を MR-005 でこの画面に限り上書き。
// 一覧は他画面と同様 page/per ページング＋最下部での無限ロード（T-002b）。

const StateOptions = [
  [Lang.all, ''] as const,
  [Lang.inHospital.inout.in, 'IN'] as const,
  [Lang.inHospital.inout.out, 'OUT'] as const,
]

// 「あいまい・複数列挙可」の RFID タグ ID 入力を配列へ（カンマ/空白区切り）。
function parseTagIds(input: string): string[] {
  return input
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

// datetime-local 値（'YYYY-MM-DDTHH:mm' / 'YYYY-MM-DDTHH:mm:ss'）を
// JST の ISO8601（+09:00）へ。MR-005=秒指定。入力時刻をそのまま送る（丸めない）。
function toJst(datetime: string): string | undefined {
  return datetime ? `${datetime}+09:00` : undefined
}

function stateLabel(state: InHospitalInoutRowComponent['state']): string {
  return state === 'IN' ? Lang.inHospital.inout.in : Lang.inHospital.inout.out
}

// 温度ロガーを `tagId（From〜To）` 形式で表示。引けない場合は '—'。
function loggerLabel(logger: InHospitalInoutRowComponent['logger']): string {
  if (!logger) return '—'
  return `${logger.tagId}（${logger.from}〜${logger.to ?? ''}）`
}

export const InHospitalInout = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [rows, setRows] = useState<InHospitalInoutRowComponent[]>([])
  const [total, setTotal] = useState<number>(0)

  const [searchTagIds, setSearchTagIds] = useState<string>('')
  const [searchFrom, setSearchFrom] = useState<string>('')
  const [searchTill, setSearchTill] = useState<string>('')
  const [searchState, setSearchState] = useState<string>('')
  const [searchMac, setSearchMac] = useState<string>('')

  const [loadNextPageAt, setLoadNextPageAt] = useState<Date>(new Date())
  const { currentPage, setPaging, stopPageLoad } = usePaging()
  const [searchParamsString, setSearchParamsString] = useState<string>()

  // 検索条件は URL の searchParams を単一の真実とする（他画面と同じ）。未指定は送らない。
  function requestFromParams() {
    const tagIds = parseTagIds(searchParams.get('tagIds') || '')
    const from = toJst(searchParams.get('from') || '')
    const till = toJst(searchParams.get('till') || '')
    const state = searchParams.get('state') || ''
    const mac = searchParams.get('mac') || ''
    return {
      ...(tagIds.length > 0 && { tagIds }),
      ...(from && { from }),
      ...(till && { till }),
      ...(state && { state: state as 'IN' | 'OUT' }),
      ...(mac && { mac }),
    }
  }

  function fetchData(loadNextPage = false) {
    return webRequest('InHospitalInoutIndex', {
      ...requestFromParams(),
      ...(loadNextPage && { page: currentPage + 1 }),
    })
  }

  async function fetchDataAndSetStates(loadNextPage = false) {
    const response = await fetchData(loadNextPage)
    if (response.status === 200 && response.data.type === 'data') {
      const data = response.data
      // 同じ検索条件なら追加読み込み（追記）、条件が変われば置き換え。
      if (searchParams.toString() === searchParamsString) {
        setRows((r) => [...r, ...data.items])
      } else {
        setRows(data.items)
      }
      setTotal(data.total)
      setPaging(data)
      setSearchParamsString(searchParams.toString())
    }
  }

  // URL（searchParams）が変わるたびに、入力欄を同期しつつ先頭ページから再取得する。
  useEffect(() => {
    setSearchTagIds((w) => w || searchParams.get('tagIds') || '')
    setSearchMac((w) => w || searchParams.get('mac') || '')
    setSearchState(searchParams.get('state') || '')
    setSearchFrom(searchParams.get('from') || '')
    setSearchTill(searchParams.get('till') || '')
    fetchDataAndSetStates()
    // eslint-disable-next-line
  }, [searchParams])

  // 最下部で次ページを取得（他画面と同様。3秒スロットル）。
  function infiniteLoad() {
    if (stopPageLoad) return
    if (moment(loadNextPageAt).isAfter(moment().subtract(3, 'seconds'))) return

    setLoadNextPageAt(new Date())
    fetchDataAndSetStates(true)
  }

  // CSV は全件を署名付き S3 URL で受け取り、その URL へ遷移してダウンロード。
  async function csvDownload() {
    const response = await webRequest('InHospitalInoutIndex', {
      ...requestFromParams(),
      csv: true,
    })
    if (response.status === 200 && response.data.type === 'url') {
      window.location.href = response.data.url
    }
  }

  // フィルタ変更 → URL を書き換えて遷移（→ useEffect で再取得）。他画面と同じ作法。
  function changeParam(key: string, value: string) {
    if (value) searchParams.set(key, value)
    else searchParams.delete(key)
    navigate(`${PATH}?${searchParams.toString()}`)
  }

  // テキスト検索（RFIDタグID / MAC）は確定操作（検索ボタン / Enter）で URL に反映する。
  function searchText() {
    if (searchTagIds) searchParams.set('tagIds', searchTagIds)
    else searchParams.delete('tagIds')
    if (searchMac) searchParams.set('mac', searchMac)
    else searchParams.delete('mac')
    navigate(`${PATH}?${searchParams.toString()}`)
  }

  const titleRightElement = (
    <div className="flex flex-col items-end">
      <SearchInput
        value={searchTagIds}
        onChange={setSearchTagIds}
        onClick={searchText}
      />
      <div className="flex mt-16 gap-[24px]">
        <div className="flex flex-col">
          <label>{Lang.inHospital.inout.state}</label>
          <Select
            className="mt-8 !w-[110px]"
            value={searchState}
            options={StateOptions}
            onChange={(value) => changeParam('state', value)}
          />
        </div>
        <div className="flex flex-col">
          <label>{Lang.inHospital.inout.mac}</label>
          <input
            type="text"
            className="w-[120px] mt-8"
            value={searchMac}
            onChange={(e) => setSearchMac(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchText()}
          />
        </div>
        <div>
          <label>{Lang.inHospital.inout.period}</label>
          <div className="flex items-center gap-[12px]">
            <input
              type="datetime-local"
              step="1"
              className="w-[220px]"
              value={searchFrom}
              onChange={(e) => changeParam('from', e.target.value)}
            />
            <span className="text-3xl text-gray-300">〜</span>
            <input
              type="datetime-local"
              step="1"
              className="w-[220px]"
              value={searchTill}
              onChange={(e) => changeParam('till', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <DefaultWithTitleLayout
      title={Lang.inHospital.inout.title}
      description={Lang.inHospital.inout.description}
      titleRightElement={titleRightElement}
      infiniteLoad={infiniteLoad}
      csvDownload={csvDownload}
    >
      {/* 表示件数インジケータ（読込済み / 総件数。例 50/1773件）。残りは下スクロールで追加読込 */}
      <div className="flex justify-end mt-32 text-sm text-gray-500">
        {rows.length}/{total}
        {Lang.inHospital.inout.countUnit}
      </div>
      <VixellTable
        rootClassName="mt-12 mb-64"
        classNames={['w-260', 'w-200', 'w-120', 'flex-1', 'w-200']}
        headers={[
          Lang.inHospital.inout.tagId,
          Lang.inHospital.inout.timestamp,
          Lang.inHospital.inout.state,
          Lang.inHospital.inout.logger,
          Lang.inHospital.inout.macColumn,
        ]}
        elements={rows.map((row, index) => [
          row.tagId,
          row.timestamp,
          stateLabel(row.state),
          <span key={index} className="px-[8px] text-center break-all">
            {loggerLabel(row.logger)}
          </span>,
          row.mac,
        ])}
      />
    </DefaultWithTitleLayout>
  )
}
